import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedLanguages, setAllLanguages } from '../redux/slices/languageSlice';
import { getSelectedLanguages, getLanguages, schoolService } from '../utils/api';
import languageService from '../utils/languageService';
import { useSchool } from '../contexts/SchoolContext';
import { useUser } from '../contexts/UserContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { extractSubdomain as extractSubdomainFromUrl } from '../utils/auth';

const LanguageSelector = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { isAuthenticated } = useUser();
  const { school } = useSchool();
  const languageState = useSelector(state => state.language);
  const selectedLanguages = languageState?.selectedLanguages || [];
  const allLanguages = languageState?.allLanguages || [];
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const processingRef = useRef(false);
  
  // Clean URL parameters if no school context using direct history API
  useEffect(() => {
    if (!school && window.location.search && window.location.search.includes('schoolId=')) {
      // When on a non-subdomain route, remove any schoolId parameter
      console.log('No school context but schoolId in URL, cleaning parameters via history API');
      try {
        const url = new URL(window.location.href);
        const params = new URLSearchParams(url.search);
        params.delete('schoolId');
        
        const newSearch = params.toString();
        const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
        
        // Use history API directly
        window.history.replaceState({}, '', newUrl);
      } catch (error) {
        console.error('Error cleaning URL parameters:', error);
      }
    }
  }, [school]);
  
  useEffect(() => {
    const fetchLanguages = async () => {
      // Prevent multiple concurrent executions
      if (processingRef.current) return;
      processingRef.current = true;
      
      try {
        setLoading(true);
        
        // Track context changes for debugging 
        const contextChange = {
          schoolChanged: languageState.currentSchoolId !== (school?.id || null),
          currentSchoolId: school?.id || null,
          prevSchoolId: languageState.currentSchoolId,
          subdomainChanged: school?.subdomain !== languageState.currentSubdomain,
          currentSubdomain: school?.subdomain || null,
          prevSubdomain: languageState.currentSubdomain
        };
        
        console.log('Language context change detected:', contextChange);
        
        // Double check that we only proceed with the current context, not stale ones
        // This prevents API calls with outdated schoolId values
        const currentPath = window.location.pathname;
        const inSubdomain = currentPath.includes('/admin') && 
                             extractSubdomainFromUrl(window.location.href);
        
        // If we're not in a subdomain but the effect thinks we are, this is stale - exit early
        if (!inSubdomain && school?.id) {
          console.log('Detected stale school context on non-subdomain route - skipping language fetch');
          processingRef.current = false;
          setLoading(false);
          return;
        }
        
        // Always store the current school/subdomain to track changes
        dispatch({ type: 'language/setCurrentSchool', payload: school?.id || null });
        dispatch({ type: 'language/setCurrentSubdomain', payload: school?.subdomain || null });
        
        // Get the user's preferred language (not temporary)
        const userPreferredLanguage = languageService.getPreferredLanguage();
        console.log('User preferred language:', userPreferredLanguage);
        
        // For non-subdomain routes (no school context), fetch all available languages
        if (!school || !school.id) {
          console.log('Fetching languages - school: null, subdomain:', school?.subdomain);
          console.log('No school context - fetching all available languages');
          
          // Clean any lingering temporary language state when on the main site
          if (languageService.getTemporaryLanguage()) {
            console.log('Cleaning up temporary language on non-school route');
            await languageService.restorePreferredLanguage(dispatch, isAuthenticated);
          }
          
          // Fetch all available languages
          const allLanguagesData = await getLanguages();
          console.log('Fetched all languages for non-school context:', allLanguagesData.length);
          
          // Store all languages in Redux
          dispatch(setAllLanguages(allLanguagesData));
          
          // When there's no school context, use all languages as selected languages
          dispatch(setSelectedLanguages(allLanguagesData));
          
          // Restore user's preferred language when not in a school context
          // This needs to always happen on non-subdomain routes
          console.log('On non-subdomain route, always restoring user preferred language');
          await languageService.restorePreferredLanguage(dispatch, isAuthenticated);
          
          setLoading(false);
          processingRef.current = false;
          return;
        }
        
        // If user is authenticated and there's a school ID, fetch school's preferred languages
        if (school.id) {
          console.log('Fetching selected languages for school:', school.id);
          try {
            // Get the default language ID directly from the school object
            const defaultLanguageId = school.default_language_id;
            console.log(`[LanguageSelector] Using school's default language ID: ${defaultLanguageId}`);
            
            // Get selected languages for the school
            const languagesData = await getSelectedLanguages(school.id);
            
            // Update Redux store with selected languages
            dispatch(setSelectedLanguages(languagesData));
            
            // Check if user's preferred language is supported by this school
            const isUserLanguageSupported = languagesData.some(
              lang => lang.code === userPreferredLanguage
            );
            
            if (isUserLanguageSupported) {
              // If user's preferred language is supported, make sure it's active
              console.log('User preferred language is supported by this school');
              if (userPreferredLanguage !== i18n.language) {
                console.log('Switching to user preferred language:', userPreferredLanguage);
                await languageService.changeLanguage(userPreferredLanguage, dispatch, isAuthenticated);
              }
            } else {
              // If user's preferred language is not supported, temporarily switch to school's default
              console.log('User preferred language is NOT supported by this school');
              
              // Find default language for this school
              const defaultLang = languagesData.find(
                lang => lang.id === defaultLanguageId
              );
              
              console.log('[LanguageSelector] Language fallback details:', {
                userPreferredLanguage,
                schoolDefaultLangId: defaultLanguageId,
                defaultLangFound: !!defaultLang,
                defaultLangCode: defaultLang?.code,
                allAvailableLanguages: languagesData.map(l => `${l.code} (${l.id})`),
                currentLanguage: i18n.language,
                schoolDefaultLanguage: defaultLang ? `${defaultLang.code} (${defaultLang.name})` : 'Not found'
              });
              
              if (defaultLang && defaultLang.code !== i18n.language) {
                console.log('[LanguageSelector] Temporarily switching to school default language:', defaultLang.code);
                // Use temporary switch to avoid saving as preference
                await languageService.temporarilySwitchLanguage(defaultLang.code, dispatch);
              } else if (!defaultLang && languagesData.length > 0) {
                // Only fallback to first available language if no default language is found at all
                console.log('[LanguageSelector] No default language found, switching to first available language:', languagesData[0].code);
                await languageService.temporarilySwitchLanguage(languagesData[0].code, dispatch);
              } else {
                console.log('[LanguageSelector] Already using the correct language:', i18n.language);
              }
            }
          } catch (error) {
            console.error('Error fetching language settings:', error);
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error in language initialization:', error);
        setLoading(false);
      }
      
      processingRef.current = false;
    };
    
    fetchLanguages();
  }, [dispatch, isAuthenticated, i18n, school, location?.search]); 
  
  const changeLanguage = async (lng) => {
    // Add debug logging to trace the authentication state
    console.log('[LanguageSelector] Change language requested:', lng, {
      isAuthenticated,
      currentUser: localStorage.getItem('user') ? 'exists' : 'not found',
      accessToken: localStorage.getItem('accessToken') ? 'exists' : 'not found'
    });
    
    try {
      // When user explicitly selects a language, save it as their preference
      await languageService.changeLanguage(lng, dispatch, isAuthenticated);
      console.log('[LanguageSelector] Language change completed for:', lng);
    } catch (error) {
      console.error('[LanguageSelector] Error changing language:', error);
    }
  };
  
  // Helper to get languages to display
  const getLanguagesToDisplay = () => {
    if (!school || !school.id) {
      // For non-school routes, use all available languages
      return allLanguages.length > 0 ? allLanguages : [
        { id: 1, code: 'en', name: 'English' },
        { id: 2, code: 'es', name: 'Spanish' },
        { id: 3, code: 'ca', name: 'Catalan' }
      ];
    }
    
    // For school-specific routes, use selected languages for that school
    return selectedLanguages;
  };
  
  // Don't render anything if we're still loading initial data
  if (loading) {
    return null;
  }
  
  const languagesToDisplay = getLanguagesToDisplay();
  
  // Always display language options
  return (
    <div className="flex space-x-2">
      {languagesToDisplay.map(language => {
        // Use school's secondary background color for selected button if available
        const selectedBgColor = school?.secondary_bg_color || '#3498DB';
        const isSelected = i18n.language === language.code;
        // Use language code as filename for consistent naming
        const flagPath = `/flags/${language.code}.png`;
        
        return (
          <button 
            key={language.id}
            onClick={() => changeLanguage(language.code)}
            className={`p-1 rounded-md ${isSelected ? 'ring-2 ring-offset-2' : 'opacity-60 hover:opacity-100'}`}
            style={{ 
              ringColor: selectedBgColor
            }}
            title={language.name || t(`languageNames.${language.code}`)}
            aria-label={language.name || t(`languageNames.${language.code}`)}
            aria-pressed={isSelected}
          >
            <img 
              src={flagPath} 
              alt={language.name || t(`languageNames.${language.code}`)}
              className="w-8 h-6 object-cover"
              style={{
                filter: isSelected ? 'none' : 'grayscale(30%)'
              }}
            />
          </button>
        );
      })}
    </div>
  );
};

export default LanguageSelector; 